import React, { useState, useEffect } from 'react'
import { accessToken } from '@/shared/helpers/CSRF'
import { PaymentElement } from '@stripe/react-stripe-js'
import { useDispatch, useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { selectCheckoutFormReset, setCheckoutFormReset } from '@/public/redux/cartSlice'
import InputMask from 'react-input-mask'
import { validationPatterns, normalizePhone } from '@/shared/helpers/Errors'
import AddressFields from '@/public/checkout/AddressFields'
import PaymentFields from '@/public/PaymentFields'
import axios from '../../../lib/httpClient'
import PolicyAgreements from './PolicyAgreements'

function hasMatchingAddress(customer) {
  if (!customer || !customer.addresses
    || !customer.addresses.billing || !customer.addresses.shipping) {
    return false
  }

  return customer.addresses.billing.single_line.replace(/\s/g, '').toLowerCase() === customer.addresses.shipping.single_line.replace(/\s/g, '').toLowerCase()
}

function getCartTotal(cart) {
  const totalAfterSavings = parseFloat(cart.calculated_total_after_savings)
  const sponsoredAllowance = parseFloat(cart.sponsored_allowance) || 0
  const supplementInvoicingAmount = parseFloat(cart.supplement_invoicing_amount) || 0

  if (totalAfterSavings <= 0) {
    return 0
  }

  return Math.max(0, totalAfterSavings - sponsoredAllowance - supplementInvoicingAmount)
}

export default function Form({
  loadingCustomerAsync = false,
  customer = null,
  states = [],
  provinces = [],
  countries = [],
  cart,
  setSelectedState,
  membershipAgreementUrl,
  hipaaPrivacyTermsUrl,
  onPaymentUnavailable,
}) {
  const {
    register, watch, trigger, setValue, formState: { errors },
  } = useFormContext()

  const dispatch = useDispatch()
  // Set by CheckoutElements when a cart change crossed the membership boundary
  // and forced the payment form to be rebuilt (SS-2918). Read from redux rather
  // than from props because the rebuild UNMOUNTS this component -- the notice
  // has to outlive the thing it is reporting on.
  const checkoutFormReset = useSelector(selectCheckoutFormReset)

  const hasMatchingAddresses = hasMatchingAddress(customer)
  const useMultipleAddresses = watch('use_multiple_addresses', (!hasMatchingAddresses || false))
  const selectedShippingState = watch('customer.shipping.state')
  const selectedBillingState = watch('customer.billing.state')
  const selectedState = useMultipleAddresses ? selectedShippingState : selectedBillingState
  const cartTotal = getCartTotal(cart)
  const [showShippingFields, setShowShippingFields] = useState(
    useMultipleAddresses || cartTotal === 0,
  )
  const [showBillingFields, setShowBillingFields] = useState(cartTotal > 0 || cart.contains_subscriptions)

  const phoneField = register('customer.phone', { required: true, pattern: validationPatterns.phone })
  const emailField = register('customer.email', { required: true, pattern: validationPatterns.email })

  const [emailFound, setEmailFound] = useState(false)

  /**
   * Dynamically clear the shipping address fields
   * if customer unchecks "ship to different address?"
   */
  useEffect(() => {
    if (useMultipleAddresses === false) {
      setValue('customer.shipping.address_line_1', '', { shouldValidate: false, shouldDirty: false })
      setValue('customer.shipping.address_line_2', '', { shouldValidate: false, shouldDirty: false })
      setValue('customer.shipping.city', '', { shouldValidate: false, shouldDirty: false })
      setValue('customer.shipping.state', '', { shouldValidate: false, shouldDirty: false })
      setValue('customer.shipping.zip', '', { shouldValidate: false, shouldDirty: false })
    }
  }, [useMultipleAddresses])

  useEffect(() => {
    setSelectedState(selectedState)
  }, [selectedState])

  useEffect(() => {
    setShowShippingFields(useMultipleAddresses || cartTotal === 0)
    setShowBillingFields(cartTotal > 0 || cart.contains_subscriptions)
  }, [cartTotal, useMultipleAddresses])

  /**
    * Prefill field values if we have them
    */
  useEffect(() => {
    setValue('customer.phone', normalizePhone(customer?.phone) || '')
    setValue('customer.email', customer?.email || '')
  }, [customer])

  async function checkEmail(email) {
    const response = await axios({
      method: 'post',
      url: '/api/orders/check_email',
      headers: {
        'Content-Type': 'application/json',
        Accepts: 'application/json',
        'X-CSRF-Token': accessToken,
      },
      data: { uuid: cart.uuid, email },
    })

    setEmailFound(response?.data?.user_found)
  }

  const paymentElementOptions = {
    fields: { billingDetails: 'never' },
    wallets: 'never',
    // Stripe.js otherwise renders a collapsed accordion with nothing selected.
    // Tabs open on Card, so the card fields show without an extra click.
    layout: 'tabs',
  }

  const shouldShowProfileFields = cart.contains_a_test || cart.contains_a_lab || cart.contains_a_membership

  return (
    <div>
      <input type="hidden" value={customer?.stripe_customer_id || ''} {...register('customer.stripe_customer_id')} />

      <div className="row mb-3">
        <div className="col-6">
          <label htmlFor="first-name" className={`form-label ${errors?.customer?.first_name ? 'error' : ''}`}>First Name</label>
          {
            // prefill & disable visible input
            Boolean(customer?.first_name) || loadingCustomerAsync ? (
              <>
                <input
                  className="form-control"
                  type="text"
                  value={customer?.first_name || ''}
                  disabled
                />
                <input
                  id="first-name"
                  type="hidden"
                  {...register('customer.first_name', { required: true, value: customer?.first_name })}
                />
              </>
            ) : (
              <input
                id="first-name"
                className={`form-control ${errors?.customer?.first_name ? 'error' : ''}`}
                type="text"
                autoComplete="given-name"
                autoCorrect="off"
                autoFocus
                {...register('customer.first_name', { required: true, value: customer?.first_name })}
              />
            )
          }
        </div>
        <div className="col-6">
          <label htmlFor="last-name" className={`form-label ${errors?.customer?.last_name ? 'error' : ''}`}>Last Name</label>
          {
            // prefill & disable visible input
            Boolean(customer?.last_name) || loadingCustomerAsync ? (
              <>
                <input
                  className="form-control"
                  type="text"
                  value={customer?.last_name || ''}
                  disabled
                />
                <input
                  id="last-name"
                  type="hidden"
                  {...register('customer.last_name', { required: true, value: customer?.last_name })}
                />
              </>
            ) : (
              <input
                id="last-name"
                className={`form-control ${errors?.customer?.last_name ? 'error' : ''}`}
                type="text"
                autoComplete="family-name"
                autoCorrect="off"
                {...register('customer.last_name', { required: true, value: customer?.last_name })}
              />
            )
          }
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-6">
          <label htmlFor="email" className={`form-label ${errors?.customer?.email ? 'error' : ''}`}>Email</label>
          {
            // prefill & disable visible input
            Boolean(customer?.email) || loadingCustomerAsync ? (
              <>
                <input
                  className="form-control"
                  type="email"
                  value={customer?.email || ''}
                  disabled
                />
                <input
                  {...emailField}
                  id="email"
                  type="hidden"
                />
              </>
            ) : (
              <input
                {...emailField}
                id="email"
                className={`form-control ${errors?.customer?.email ? 'error' : ''}`}
                type="email"
                autoComplete="email"
                autoCorrect="off"
                onBlur={(e) => {
                  emailField.onBlur(e)
                  checkEmail(e.target.value)
                  trigger('customer.email')
                }}
              />
            )
          }
        </div>
        <div className="col-6">
          <label htmlFor="phone" className={`form-label ${errors?.customer?.phone ? 'error' : ''}`}>Phone Number</label>
          {
            // prefill & disable visible input (only if phone normalizes to a valid format)
            customer?.phone && validationPatterns.phone.value.test(normalizePhone(customer.phone)) ? (
              <>
                <InputMask
                  mask="999-999-9999"
                  maskchar={null}
                  alwaysShowMask
                  defaultValue={normalizePhone(customer.phone)}
                  disabled
                >
                  <input className="form-control" type="tel" />
                </InputMask>
                <input
                  {...phoneField}
                  id="phone"
                  type="hidden"
                />
              </>
            ) : (
              <InputMask
                mask={watch('customer.phone') ? '999-999-9999' : null}
                maskchar={null}
                alwaysShowMask
                defaultValue={watch('customer.phone', customer?.phone || '')}
                onChange={(e) => setTimeout(() => phoneField.onChange(e), 250)}
                onBlur={(e) => { phoneField.onChange(e) }}
                onInput={(e) => { phoneField.onChange(e) }}
              >
                <input
                  ref={phoneField.ref}
                  id="phone"
                  name={phoneField.name}
                  className={`form-control ${errors?.customer?.phone ? 'error' : ''}`}
                  type="tel"
                  autoComplete="tel-national"
                  autoCorrect="off"
                />
              </InputMask>
            )
          }
        </div>
      </div>

      {emailFound && (
        <p className="p-4 pt-2">
          We already have an account for this email.
          Login to checkout faster and view your orders.
          &nbsp;&nbsp;
          <a href="/account/login" className="btn btn-primary" style={{ lineHeight: '10px' }}>Login</a>
        </p>
      )}

      {shouldShowProfileFields && (
        <div className="row mb-3">
          <div className="col-6">
            <label htmlFor="date-of-birth" className="form-label">Date of Birth</label>
            <input
              id="date-of-birth"
              type="date"
              className={`form-control ${errors?.customer?.date_of_birth ? 'error' : ''}`}
              {...register('customer.date_of_birth', {
                required: 'Your cart contains an age restricted item',
                validate: {
                  isAdult: (value) => {
                    const dob = new Date(value)
                    const now = new Date()
                    const eighteenYearsAgo = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate())

                    return dob <= eighteenYearsAgo || 'You must be at least 18 years old to order a lab or membership'
                  },
                },
              })}
              defaultValue={customer?.date_of_birth}
            />
            {errors?.customer?.date_of_birth && (
              <span className="text-danger">{errors.customer.date_of_birth.message}</span>
            )}
          </div>
          <div className="col-6">
            <label htmlFor="sex" className="form-label">Sex</label>
            {/* `react` opts out of scripts/custom.js's customSelect, which hides a
                native select it finds and reports choices with a jQuery-only
                change event that react-hook-form never receives. */}
            <select
              id="sex"
              className={`form-control react ${errors?.customer?.sex ? 'error' : ''}`}
              {...register('customer.sex', { required: 'Sex is required to order a lab or membership' })}
              defaultValue={customer?.sex}
            >
              <option value="">Select</option>
              <option value="F">Female</option>
              <option value="M">Male</option>
              <option value="?">Prefer not to say</option>
            </select>
            {errors?.customer?.sex && (
              <span className="text-danger">{errors.customer.sex.message}</span>
            )}
          </div>
        </div>
      )}

      {(cart.contains_a_membership || cart.contains_a_virtual_visit) && (
        <PolicyAgreements
          cart={cart}
          errors={errors}
          register={register}
          membershipAgreementUrl={membershipAgreementUrl}
          hipaaPrivacyTermsUrl={hipaaPrivacyTermsUrl}
        />
      )}

      {showBillingFields && <AddressFields customer={customer} states={states} provinces={provinces} countries={countries} type="billing" />}

      {showShippingFields && <AddressFields customer={customer} states={states} provinces={provinces} countries={countries} type="shipping" />}

      {cartTotal > 0
        && (
          <div className="row mt-5 mb-3">
            <div className="col-12">
              <div className="form-check">
                <input
                  id="use_multiple_addresses"
                  className="form-check-input"
                  type="checkbox"
                  defaultChecked={!hasMatchingAddresses}
                  {...register('use_multiple_addresses')}
                />
                <label htmlFor="use_multiple_addresses" className="form-check-label">
                  Ship to a different address?
                </label>
              </div>
            </div>
          </div>
        )}
      <hr className="line my-5" />

      {checkoutFormReset && (
        <div className="row mb-3">
          <div className="col">
            <div className="alert alert-warning mb-0" role="alert">
              Your cart changed, so we had to rebuild the payment form and it
              cleared what you had entered. Please fill in your details again
              before placing your order.
            </div>
          </div>
        </div>
      )}

      {cartTotal > 0 && (
        <div className="row mb-3">
          <div className="col">
            <PaymentFields source="checkout.payment" onUnavailable={onPaymentUnavailable}>
              {(onLoadError) => (
                <PaymentElement
                  options={paymentElementOptions}
                  onLoadError={onLoadError}
                  // Payment is the last thing on the form, so touching it means the
                  // guest has worked back down through everything the rebuild
                  // cleared -- the right moment to stop telling them about it.
                  onChange={() => checkoutFormReset && dispatch(setCheckoutFormReset(false))}
                />
              )}
            </PaymentFields>
          </div>
        </div>
      )}
    </div>
  )
}
